import type { IHierarchyViewSelectItem } from 'components/HierarchyViewSelect/HierarchyViewSelect';
import type { WorkOrderChangeObjectItem, WorkOrderObjectData, WorkOrderVersionObjectData } from 'gql/graphql';
import { Combobox, Grid, GridItem } from 'uikit';

import React, { useCallback, useMemo } from 'react';

import { ConstructionObjectCombobox, ConstructionSubObjectCombobox } from '../ConstructionObjectCombobox';
import { WorkOrderChapters } from '../workorderTypes';

interface IWorkOrderFilterFormProps {
    constructionTypeId: number;
    defaultValues: any;
    onChange: (filters: any) => void;
    workOrderObjects:
        | (WorkOrderObjectData | WorkOrderVersionObjectData | WorkOrderChangeObjectItem)[]
        | null
        | undefined;
}

export const WorkOrderFilterForm = ({
    constructionTypeId,
    defaultValues,
    onChange,
    workOrderObjects,
}: IWorkOrderFilterFormProps) => {
    const handleObjectChange = useCallback(
        ({ value }) => {
            const filters = { objectId: value?.id, subObjectId: undefined };

            const objects = value?.id && workOrderObjects ? workOrderObjects : [];

            const isAreThereSubObjects = !!objects.filter(
                ({ constructionType, rootObjectId }) =>
                    rootObjectId === value?.id && constructionType.id === constructionTypeId,
            ).length;

            if (value?.id && workOrderObjects && !isAreThereSubObjects) {
                filters.subObjectId = value?.id;
            }

            onChange(filters);
        },
        [constructionTypeId, onChange, workOrderObjects],
    );

    const handleSubObjectChange = useCallback(
        (selectedItem: IHierarchyViewSelectItem | undefined) => {
            onChange({ subObjectId: selectedItem?.id });
        },
        [onChange],
    );

    const handleChapterChange = useCallback(
        ({ value }) => {
            onChange({ chapter: value?.id });
        },
        [onChange],
    );

    const chaptersItems = useMemo(
        () => [
            { id: WorkOrderChapters.CHAPTER1_7, label: 'Глава 1-7' },
            { id: WorkOrderChapters.CHAPTER8, label: 'Глава 8' },
            { id: WorkOrderChapters.CHAPTER9, label: 'Глава 9' },
            { id: WorkOrderChapters.OTHERS, label: 'Прочие' },
            { id: WorkOrderChapters.SUMMARY, label: 'Итоги НЗ' },
        ],
        [],
    );

    const chapterValue = useMemo(
        () => chaptersItems.find(({ id }) => id === defaultValues?.chapter),
        [chaptersItems, defaultValues],
    );

    const isSummaryChapter = useMemo(() => chapterValue?.id === chaptersItems[4].id, [chapterValue, chaptersItems]);
    const isOther = useMemo(() => chapterValue?.id === chaptersItems[3].id, [chapterValue, chaptersItems]);

    return (
        <form>
            <Grid cols={8} gap="xl" rowGap="xl" colGap="xl">
                <GridItem col={2}>
                    <Combobox
                        placeholder="Раздел"
                        items={chaptersItems}
                        value={chapterValue}
                        onChange={handleChapterChange}
                        size="s"
                    />
                </GridItem>
                <GridItem col={3}>
                    <ConstructionObjectCombobox
                        constructionTypeId={constructionTypeId}
                        objectId={defaultValues?.objectId}
                        onChange={handleObjectChange}
                        disabled={isSummaryChapter}
                        workOrderObjects={workOrderObjects}
                    />
                </GridItem>
                <GridItem col={3}>
                    <ConstructionSubObjectCombobox
                        constructionTypeId={constructionTypeId}
                        objectId={defaultValues?.objectId}
                        subObjectId={defaultValues?.subObjectId}
                        onChange={handleSubObjectChange}
                        disabled={isSummaryChapter || isOther}
                        workOrderObjects={workOrderObjects}
                    />
                </GridItem>
            </Grid>
        </form>
    );
};

WorkOrderFilterForm.displayName = 'WorkOrderFilterForm';
